import { createContext, useContext, useState, type Dispatch, type ReactNode, type SetStateAction } from 'react';
import type { Competence, IrrfDeductionMode, OvertimeInput, SalaryBonus, SalaryInput } from '@amigodoclt/core';

export type OvertimeRow = OvertimeInput & { id: string };
export type CustomDeduction = { id: string; label: string; amount: number };

export type SalaryForm = {
  competence: Competence;
  base: number;
  dependents: number;
  pension: number;
  hours: number;
  nightHours: number;
  nightPremium: number;
  bonusType: SalaryBonus['type'];
  bonusValue: number;
  vtEnabled: boolean;
  vtPercent: number;
  irrfMode: IrrfDeductionMode;
  period: 'monthly' | 'biweekly';
  advancePercent: number;
  overtimes: OvertimeRow[];
  deductions: CustomDeduction[];
};

export const defaultSalaryForm: SalaryForm = {
  competence: 2026, base: 5000, dependents: 0, pension: 0, hours: 220, nightHours: 0, nightPremium: 20,
  bonusType: 'fixed', bonusValue: 0, vtEnabled: false, vtPercent: 6, irrfMode: 'auto',
  period: 'monthly', advancePercent: 40, overtimes: [], deductions: [],
};

/** O comparador usa as mesmas premissas do salário, então o formulário vive acima das rotas. */
const SalaryFormContext = createContext<[SalaryForm, Dispatch<SetStateAction<SalaryForm>>] | null>(null);

export function SalaryFormProvider({ children }: { children: ReactNode }) {
  const state = useState(defaultSalaryForm);
  return <SalaryFormContext.Provider value={state}>{children}</SalaryFormContext.Provider>;
}

export function useSalaryForm() {
  const state = useContext(SalaryFormContext);
  if (!state) throw new Error('useSalaryForm exige o SalaryFormProvider.');
  return state;
}

/** A entrada do core é a mesma nas duas telas; o comparador só troca o salário-base. */
export const salaryInputOf = (form: SalaryForm, grossSalary = form.base): SalaryInput => ({
  competence: form.competence, grossSalary, dependents: form.dependents, pension: form.pension,
  hoursPerMonth: form.hours,
  ...(form.bonusValue > 0 ? { bonus: { type: form.bonusType, value: form.bonusValue } } : {}),
  ...(form.vtEnabled ? { transitBenefitPercent: form.vtPercent } : {}),
  irrfDeductionMode: form.irrfMode, nightHours: form.nightHours, nightPremiumPercent: form.nightPremium,
  overtime: form.overtimes.map((row) => ({ label: row.label, hours: row.hours, premiumPercent: row.premiumPercent, dsrPercent: row.dsrPercent })),
  customDeductions: form.deductions.map((row) => ({ label: row.label, amount: row.amount })),
});
